import type { DocumentId, NetworkAdapter, StorageAdapter } from "@automerge/automerge-repo";
import { Repo } from "@automerge/automerge-repo";
import { IndexedDBStorageAdapter } from "@automerge/automerge-repo-storage-indexeddb";
import { Application, Container, Graphics, Text } from "pixi.js";
import type { SkeinTheme } from "../theme/skein-theme";
import { defaultTheme } from "../theme/skein-theme";
import { KeyboardDriver } from "../widgets/keyboard-driver";
import type { WidgetRegistry } from "../widgets/widget-registry";
import { CanvasBlobAclSync } from "./blob-acl-sync";
import { CanvasStore } from "./canvas-store";
import { ConnectionStatus } from "./connection-status";
import { InputRouter } from "./input-router";
import { LassoTool } from "./lasso-tool";
import { PresenceManager } from "./presence-manager";
import { PresenceRenderer } from "./presence-renderer";
import { PropertyTray } from "./property-tray";
import type { BreadcrumbItem } from "./toolbar";
import { Toolbar } from "./toolbar";
import { Viewport } from "./viewport";
import { WidgetManager } from "./widget-manager";
import { attachClipboardCursor } from "./clipboard-cursor";

export interface InitCanvasOptions {
  /** DOM element to mount the PixiJS canvas into */
  mountElement: HTMLElement;
  /** existing canvas document ID to open, or null to create new */
  canvasDocId: string | null;
  /** widget factory registry */
  registry: WidgetRegistry;
  /** the FULL, regular-canvas widget registry — always this, even when
   *  `registry` above is a narrower one (e.g. narthex's own
   *  `createNarthexRegistry()`). needed for cross-canvas operations that
   *  must resolve an arbitrary widget type on some OTHER canvas (see
   *  `WidgetMountContext.crossCanvasRegistry`'s own doc comment). falls
   *  back to `registry` when omitted, so existing callers (test harnesses,
   *  dev tools) that only ever use one registry anyway don't need to
   *  change. */
  crossCanvasRegistry?: WidgetRegistry;
  /** optional network adapter (BroadcastChannel for tests, iroh for prod) */
  networkAdapter?: NetworkAdapter;
  /** optional storage adapter (defaults to IndexedDB) */
  storageAdapter?: StorageAdapter;
  /** optional theme overrides */
  theme?: Partial<SkeinTheme>;
  /** optional pre-existing automerge repo — when provided, storage/network options are ignored */
  repo?: Repo;
  /** if true, this canvas is the narthex (home screen) — affects toolbar behavior */
  isNarthex?: boolean;
  /** callback to navigate back to the narthex — toolbar shows a home button when set */
  onNavigateHome?: () => void;
  /**
   * ancestor canvas breadcrumbs (narthex -> ... -> immediate parent), fed
   * by the app-level router's cross-canvas navigation history — see
   * `WidgetManager.setAncestorCrumbs()`'s doc comment. omitted (or empty)
   * when this canvas has no navigation history (e.g. a cold open, or the
   * narthex itself).
   */
  ancestorCrumbs?: BreadcrumbItem[];
  /** callback to share the current canvas — toolbar shows a share button when set */
  onShare?: () => void;
  /** callback to toggle the social overlay panel — toolbar shows avatar button when set */
  onToggleSocial?: () => void;
  /** callback to toggle the messages overlay panel — toolbar shows messages button when set */
  onToggleMessages?: () => void;
  /** callback to toggle the filez (transfers) overlay panel — toolbar shows filez button when set */
  onToggleFilez?: () => void;
  /** whether the user has a configured identity — controls share button visibility */
  hasIdentity?: boolean;
  /** pre-seeded avatar URL — shown immediately if cached, avoiding the gradient flash */
  avatarUrl?: string | null;
  /** iroh endpoint state source — drives the status dot on the avatar toolbar button */
  endpointStateSource?: {
    getState(): "off" | "starting" | "online" | "error";
    onStateChange(h: (state: "off" | "starting" | "online" | "error") => void): () => void;
  };
  /** callback to show the canvas-info overlay — triggered by connection-status pill click */
  onShowCanvasInfo?: () => void;
  /** optional transport-level connection state source for the status indicator */
  connectionStateSource?: import("./connection-status").ConnectionStateSource | null;
  /** optional per-widget doc overrides — lets callers inject a custom doc (e.g.
   *  SqliteSocialDoc) for specific widget IDs instead of the automerge-backed one */
  docOverrides?: Map<string, import("../widgets/widget-types").WidgetDoc<any>>;
  /**
   * optional hook that restricts a blob (by blake3 hash) to a given set of
   * peer node ids at the `iroh-blobs/*` transport layer — see
   * `restrictBlobToPeers()` in `p2p/iroh-network-adapter.ts`. when provided,
   * the canvas keeps every file widget's blob allow-list in sync with this
   * canvas's `.acl` (see `blob-acl-sync.ts`) for as long as the canvas stays
   * open. omitted in test harnesses/narthex init that have no real blob
   * transport to gate, or nothing worth gating (the narthex has no file
   * widgets).
   */
  restrictBlobToPeers?: (blake3Hash: string, peerNodeIds: string[]) => Promise<void>;
  /**
   * bounds `CanvasStore.open()`'s wait with a short `AbortSignal`-based
   * timeout instead of its own default ~60s bound — see `CanvasStore.open()`'s
   * doc comment for when this is appropriate (a cold open with no known peer
   * to dial at all). omitted for ordinary in-app navigation.
   */
  openTimeoutMs?: number;
  /**
   * lets a caller cancel `CanvasStore.open()`'s wait on demand — e.g. a
   * user clicking "cancel" on a loading screen shown while this is in
   * flight. distinct from `openTimeoutMs`: both bound the same wait, but
   * this one fires on user action rather than a fixed deadline.
   */
  openAbortSignal?: AbortSignal;
}

export interface SkeinCanvas {
  /** the canvas store for reading/mutating the canvas document */
  store: CanvasStore;
  /** the widget registry */
  registry: WidgetRegistry;
  /** the widget manager that bridges store data to pixi rendering */
  widgetManager: WidgetManager;
  /** the input router for mode switching and selection */
  inputRouter: InputRouter;
  /** the toolbar (pixi-rendered, top-right of stage) */
  toolbar: Toolbar;
  /** the keyboard driver for text input / IME (hidden textarea proxy) */
  keyboard: KeyboardDriver;
  /** the viewport for pan/zoom control */
  viewport: Viewport;
  /** the world container that holds all widgets (affected by pan/zoom) */
  world: Container;
  /** the presence manager for multiplayer cursor/lock/selection state */
  presenceManager: PresenceManager;
  /** the presence renderer for remote peer cursors (null on narthex) */
  presenceRenderer: PresenceRenderer | null;
  /** the connection status indicator (peer count pill, null on narthex) */
  connectionStatus: ConnectionStatus | null;
  /** the property editing tray (appears next to selected widget in edit mode) */
  propertyTray: PropertyTray;
  /** the lasso tool for multi-select, click-deselect, and double-click-to-add */
  lassoTool: LassoTool;
  /** the PixiJS application instance */
  app: Application;
  /** the resolved theme */
  theme: SkeinTheme;
  /** the automerge repo */
  repo: Repo;
  /** this peer's unique id (from the automerge repo) */
  peerId: string;
  /** update the notification badge count on the avatar (social) toolbar button */
  updateSocialBadge?: (count: number) => void;
  /** update the notification badge count on the messages toolbar button */
  updateMessagesBadge?: (count: number) => void;
  /** tear down the canvas (cleanup pixi, widgets, toolbar, viewport, presence, input router) */
  destroy: () => void;
}

/**
 * initialize a skein canvas. this is the single entry point --
 * one linear async function, no polling, no globals, no timeouts.
 *
 * call this once. it returns a SkeinCanvas handle with everything
 * you need to interact with the canvas. call handle.destroy() to clean up.
 */
export async function initCanvas(options: InitCanvasOptions): Promise<SkeinCanvas> {
  const {
    mountElement,
    canvasDocId,
    registry,
    networkAdapter,
    storageAdapter,
    theme: themeOverrides,
  } = options;
  const crossCanvasRegistry = options.crossCanvasRegistry ?? registry;

  // step 1: resolve theme by merging overrides onto defaults
  const theme: SkeinTheme = { ...defaultTheme, ...themeOverrides };

  // step 2: use the provided repo or create a new one
  let repo: Repo;
  if (options.repo) {
    repo = options.repo;
  } else {
    const storage = storageAdapter ?? new IndexedDBStorageAdapter();
    const network = networkAdapter ? [networkAdapter] : [];
    repo = new Repo({ storage, network });
  }

  // step 3: load or create canvas document
  let store: CanvasStore;
  if (canvasDocId) {
    store = await CanvasStore.open(repo, canvasDocId as DocumentId, {
      timeoutMs: options.openTimeoutMs,
      signal: options.openAbortSignal,
    });
  } else {
    store = CanvasStore.create(repo);
  }

  // step 3b: keep this canvas's file-widget blobs gated to its current
  // `.acl` for as long as the canvas is open (see blob-acl-sync.ts). skipped
  // when no gating hook was provided (test harnesses with no real blob
  // transport) or on the narthex (never has file widgets, nothing to gate).
  let blobAclSync: CanvasBlobAclSync | null = null;
  if (options.restrictBlobToPeers && !options.isNarthex) {
    blobAclSync = new CanvasBlobAclSync(store, repo, options.restrictBlobToPeers);
    blobAclSync.start();
  }

  // step 4: create and initialize pixi application
  const app = new Application();
  await app.init({
    resizeTo: mountElement,
    background: theme.stageBg,
    antialias: true,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
  });

  // step 5: mount canvas into DOM
  mountElement.appendChild(app.canvas);

  // step 5a: the real canvas is now actually painting — remove the
  // css-only boot spinner (index.html) that's been covering the initial
  // blank screen since page load, while the midden p2p node/worker and
  // this canvas's document were still coming up. safe to call unconditionally
  // (every render path — narthex or a specific canvas — funnels through this
  // one function before anything else is visible), and a no-op past the
  // first call since the element is gone after the first removal.
  document.getElementById("boot-spinner")?.remove();

  // step 5b: create keyboard driver (the one hidden HTML element — a <textarea>
  // used as a proxy for text input, IME composition, and clipboard)
  const keyboard = new KeyboardDriver(app.canvas as HTMLCanvasElement);

  // step 6: create world container — sits between app.stage and widgets.
  // the viewport translates/scales this container for pan and zoom.
  // the toolbar and other HUD elements live directly on app.stage
  // so they stay fixed regardless of viewport transforms.
  const world = new Container();
  world.sortableChildren = true;
  app.stage.addChild(world);

  // step 7: draw stage background inside the world container so it
  // pans and zooms with everything else
  const stageBg = new Graphics();
  stageBg.rect(-2000, -2000, 4000, 4000);
  stageBg.fill({ color: theme.stageBg });
  stageBg.zIndex = -1;
  world.addChild(stageBg);

  // step 7b: draw a subtle grid over the stage background.
  // covers a fixed region (-1000,-1000) to (1000,1000) — enough for
  // typical canvas use. the grid lives in the world container so it
  // pans and zooms with everything else.
  const stageGrid = new Graphics();
  const gridSize = theme.gridSize;
  const gridMin = -1000;
  const gridMax = 1000;
  stageGrid.setStrokeStyle({ width: 1, color: theme.stageGrid, alpha: 0.3 });
  for (let gx = gridMin; gx <= gridMax; gx += gridSize) {
    stageGrid.moveTo(gx, gridMin);
    stageGrid.lineTo(gx, gridMax);
  }
  for (let gy = gridMin; gy <= gridMax; gy += gridSize) {
    stageGrid.moveTo(gridMin, gy);
    stageGrid.lineTo(gridMax, gy);
  }
  stageGrid.stroke();
  stageGrid.zIndex = -1;
  world.addChild(stageGrid);

  // step 8: create input router (mode switching, selection, keyboard shortcuts)
  const inputRouter = new InputRouter();

  // make the stage background interactive so the lasso tool can attach to it.
  // the stageBg is the lowest zIndex element in the world container, so
  // pointer events only reach it when they don't hit any widget frame or
  // other interactive element (they all stopPropagation on pointerdown).
  stageBg.eventMode = "static";

  // step 9: create and start the widget manager.
  // widget frames are added to the world container so they participate in pan/zoom.
  const widgetManager = new WidgetManager(
    store,
    registry,
    crossCanvasRegistry,
    repo,
    world,
    theme,
    inputRouter,
    keyboard,
    app.canvas as HTMLCanvasElement,
    stageBg
  );

  // apply doc overrides before start() so widgets mount with the right doc
  if (options.docOverrides) {
    for (const [widgetId, doc] of options.docOverrides) {
      widgetManager.setDocOverride(widgetId, doc);
    }
  }

  // step 10: create the toolbar (pixi-rendered, top-right of stage).
  // added directly to app.stage by the Toolbar constructor so it stays fixed.
  const toolbar = new Toolbar(app, inputRouter, store, registry, theme, {
    isNarthex: options.isNarthex,
    onNavigateHome: options.onNavigateHome,
    onShare: options.onShare,
    onToggleSocial: options.onToggleSocial,
    onToggleMessages: options.onToggleMessages,
    onToggleFilez: options.onToggleFilez,
    hasIdentity: options.hasIdentity,
    avatarUrl: options.avatarUrl,
    endpointStateSource: options.endpointStateSource,
  });

  // step 10a: wire toolbar into widget manager for breadcrumb navigation.
  // the widget manager pushes breadcrumb updates on maximize/restore and
  // whenever widget titles or canvas title change. must happen before start()
  // so the first reconcile() → updateBreadcrumbs() call sees the toolbar.
  widgetManager.setToolbar(toolbar);
  if (options.onNavigateHome) {
    widgetManager.setNavigateHome(options.onNavigateHome);
  }
  if (options.ancestorCrumbs?.length) {
    widgetManager.setAncestorCrumbs(options.ancestorCrumbs);
  }

  widgetManager.start();

  // step 10b: create the lasso tool for multi-select, click-deselect, and
  // double-click-to-add. it attaches pointer handlers to the stage background
  // and manages freeform lasso selection drawing in the world container.
  const lassoTool = new LassoTool({
    target: stageBg,
    world,
    inputRouter,
    widgetManager,
    theme,
    onDoubleClick: (screenX, screenY, worldX, worldY) => {
      toolbar.openFlyoutAtPosition(screenX, screenY, worldX, worldY);
    },
    onLassoStart: () => {
      widgetManager.setLassoActive(true);
    },
    onLassoEnd: () => {
      widgetManager.setLassoActive(false);
    },
  });

  // step 11: create viewport for pan/zoom control over the world container
  const viewport = new Viewport(world, app.canvas as HTMLCanvasElement);

  // step 11b: give the widget manager a viewport reference so maximize/restore
  // can save and restore camera state
  widgetManager.setViewport(viewport);

  // step 11c: persist the narthex's pan/zoom across visits, keyed by canvas
  // doc id, via localStorage (deliberately not synced through automerge —
  // each peer keeps their own camera position, not a shared one). regular
  // canvases don't get this treatment, only the narthex home screen.
  let unsubViewportPersist: (() => void) | null = null;
  let viewportSaveTimer: ReturnType<typeof setTimeout> | null = null;
  if (options.isNarthex && canvasDocId) {
    const viewportStorageKey = `skein-narthex-viewport:${canvasDocId}`;
    try {
      const saved = localStorage.getItem(viewportStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as { x: number; y: number; zoom: number };
        // `stageBg` (the double-click-to-add-widget hit target, also the
        // only thing making the canvas look non-blank) is a fixed 4000x4000
        // rect in WORLD coordinates, itself a child of `world` — so a
        // corrupted/out-of-range x or y here doesn't just look wrong, it
        // pans that rect (and every widget) entirely out of view AND out of
        // the double-click hit area, with no error thrown anywhere (the
        // canvas just renders blank and clicks hit nothing). guard against
        // ever restoring a value that broken, and drop the bad entry so it
        // can't wedge this canvas again on the next visit.
        const MAX_NARTHEX_CAMERA_COORD = 20000;
        if (
          typeof parsed.x === "number" &&
          typeof parsed.y === "number" &&
          typeof parsed.zoom === "number" &&
          Number.isFinite(parsed.x) &&
          Number.isFinite(parsed.y) &&
          Number.isFinite(parsed.zoom) &&
          Math.abs(parsed.x) < MAX_NARTHEX_CAMERA_COORD &&
          Math.abs(parsed.y) < MAX_NARTHEX_CAMERA_COORD
        ) {
          viewport.zoomTo(parsed.zoom);
          viewport.panTo(parsed.x, parsed.y);
        } else {
          localStorage.removeItem(viewportStorageKey);
        }
      }
    } catch {
      // malformed/missing localStorage entry — just start at the default view
    }

    unsubViewportPersist = viewport.onViewChange(() => {
      if (viewportSaveTimer !== null) clearTimeout(viewportSaveTimer);
      viewportSaveTimer = setTimeout(() => {
        viewportSaveTimer = null;
        try {
          localStorage.setItem(
            viewportStorageKey,
            JSON.stringify({ x: viewport.cameraX, y: viewport.cameraY, zoom: viewport.zoom })
          );
        } catch {
          // localStorage unavailable/full — not critical, just skip persisting
        }
      }, 400);
    });
  }

  // step 12: create the presence manager for multiplayer awareness.
  // uses the repo's peer id so ephemeral message sender ids match.
  const peerId = repo.peerId as string;
  const presenceManager = new PresenceManager(store, peerId);

  // step 13: create the presence renderer for remote peer cursors.
  // lives in the world container so cursors pan/zoom with widgets.
  // skipped on the narthex — no remote cursors needed on the home screen.
  let presenceRenderer: PresenceRenderer | null = null;
  let connectionStatus: ConnectionStatus | null = null;

  if (!options.isNarthex) {
    presenceRenderer = new PresenceRenderer(world, presenceManager, theme);

    // step 13b: create the connection status indicator.
    // lives on app.stage (fixed position, bottom-left) so it doesn't pan/zoom.
    // layout() reads visual viewport internally for correct sizing on mobile safari.
    connectionStatus = new ConnectionStatus(
      presenceManager,
      theme,
      options.connectionStateSource,
      options.onShowCanvasInfo
    );
    app.stage.addChild(connectionStatus.root);
    connectionStatus.layout();
  }

  // step 13a-resize: keep the top-right toolbar and bottom-left connection-
  // status pill correctly positioned when the window is resized. pixi's own
  // `resizeTo` plugin (app.init({resizeTo: mountElement})) only resizes the
  // renderer/canvas itself — it never re-runs either widget's own layout(),
  // which is what actually repositions them against the new screen bounds.
  // debounced (rAF-coalesced) so a drag-resize of the window doesn't thrash
  // layout on every intermediate `resize` event.
  let resizeRaf: number | null = null;
  const handleWindowResize = () => {
    if (resizeRaf !== null) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = null;
      toolbar.layout();
      connectionStatus?.layout();
    });
  };
  window.addEventListener("resize", handleWindowResize);

  // step 13b-sync: loading overlay — shown when the canvas doc is empty (syncing).
  // fades out automatically when the first widget appears.
  let syncOverlay: Container | null = null;
  if (!options.isNarthex && store.widgetCount() === 0) {
    syncOverlay = new Container();
    syncOverlay.zIndex = 9000;

    const syncLabel = new Text({
      text: "syncing canvas...",
      resolution: theme.textResolution,
      style: {
        fontFamily: theme.fontFamily,
        fontSize: 16,
        fill: theme.frameHeaderText,
        fontStyle: "italic",
      },
    });
    syncLabel.anchor.set(0.5);
    syncOverlay.addChild(syncLabel);

    // position in center of screen
    const vv = window.visualViewport;
    const screenW = vv ? vv.width : window.innerWidth;
    const screenH = vv ? vv.height : window.innerHeight;
    syncLabel.x = screenW / 2;
    syncLabel.y = screenH / 2;

    app.stage.addChild(syncOverlay);

    // pulse animation
    const syncOverlayTimer: ReturnType<typeof setInterval> | undefined = setInterval(() => {
      if (!syncOverlay) return;
      syncOverlay.alpha = 0.4 + Math.abs(Math.sin(Date.now() / 800)) * 0.6;
    }, 50);

    // auto-remove when widgets appear
    const removeSyncOverlay = () => {
      if (syncOverlay && store.widgetCount() > 0) {
        clearInterval(syncOverlayTimer);
        syncOverlay.destroy({ children: true });
        syncOverlay = null;
        unsubSyncOverlay();
      }
    };
    const unsubSyncOverlay = store.onChange(() => removeSyncOverlay());
  }

  // step 13c: create the property tray for editing widget props.
  // lives in the world container so it pans/zooms with widgets.
  // subscribes to selection/mode/store changes internally.
  const propertyTray = new PropertyTray(
    world,
    theme,
    app.canvas as HTMLCanvasElement,
    inputRouter,
    widgetManager,
    store,
    registry
  );

  // step 13c-drag: keep the tray glued to the selected widget's *live*
  // frame position while it's being dragged — store.onChange() (which
  // repositionIfNeeded() is already subscribed to internally) only fires
  // once the drag commits on release, not on every intermediate move.
  const unsubTrayLiveMove = widgetManager.onLiveMove(() => propertyTray.repositionIfNeeded());

  // keep the tray's on-screen size constant regardless of
  // viewport zoom (it lives in world space so it pans with the widget, but
  // its buttons/text shouldn't shrink to unusable sizes when zoomed out —
  // see PropertyTray.setZoom()).
  propertyTray.setZoom(viewport.zoom);
  const unsubTrayZoom = viewport.onZoomChange((zoom) => propertyTray.setZoom(zoom));

  // step 14: track local cursor movement and broadcast via presence manager.
  // skipped on the narthex — no cursor broadcasting needed on the home screen.
  const canvasEl = app.canvas as HTMLCanvasElement;
  let onPointerMove: ((e: PointerEvent) => void) | null = null;

  // the clipboard-follower badge is independent of presence/narthex — it
  // should show anywhere there's copied skein widget content, including
  // the narthex itself, and survives navigating between canvases since
  // this whole function re-runs (and re-attaches) on every canvas open.
  const clipboardCursorCleanup = attachClipboardCursor(app, canvasEl, theme);

  if (!options.isNarthex) {
    // we listen on the canvas element for pointermove and convert screen
    // coordinates to world coordinates so remote peers see the correct position.
    onPointerMove = (e: PointerEvent) => {
      const { x: worldX, y: worldY } = viewport.screenToWorld(e.clientX, e.clientY);
      presenceManager.broadcastCursor(worldX, worldY);
    };
    canvasEl.addEventListener("pointermove", onPointerMove);

    // step 15: announce that we're online
    presenceManager.broadcastOnline();
  }

  // capture the canvas element before destroy nulls out pixi internals
  const canvasElement = app.canvas;

  // step 16: return the handle
  return {
    store,
    registry,
    widgetManager,
    inputRouter,
    toolbar,
    keyboard,
    viewport,
    world,
    presenceManager,
    presenceRenderer,
    connectionStatus,
    propertyTray,
    lassoTool,
    app,
    theme,
    repo,
    peerId,
    updateSocialBadge: (n: number) => (toolbar as any).updateSocialBadge?.(n),
    updateMessagesBadge: (n: number) => (toolbar as any).updateMessagesBadge?.(n),
    destroy() {
      // best-effort offline broadcast before teardown
      presenceManager.broadcastOffline();

      blobAclSync?.destroy();

      window.removeEventListener("resize", handleWindowResize);
      if (resizeRaf !== null) {
        cancelAnimationFrame(resizeRaf);
        resizeRaf = null;
      }
      unsubTrayLiveMove();
      unsubTrayZoom();
      unsubViewportPersist?.();
      if (viewportSaveTimer !== null) {
        clearTimeout(viewportSaveTimer);
        viewportSaveTimer = null;
      }

      if (onPointerMove) {
        canvasEl.removeEventListener("pointermove", onPointerMove);
      }
      clipboardCursorCleanup();
      if (syncOverlay) {
        syncOverlay.destroy({ children: true });
        syncOverlay = null;
      }
      connectionStatus?.destroy();
      presenceRenderer?.destroy();
      presenceManager.destroy();
      lassoTool.destroy();
      propertyTray.destroy();
      toolbar.destroy();
      keyboard.destroy();
      viewport.destroy();
      inputRouter.destroy();
      widgetManager.destroyAll();
      app.destroy(true, { children: true });
      // pixi v8 nulls internals on destroy, so we use the captured ref
      if (canvasElement.parentNode) {
        canvasElement.parentNode.removeChild(canvasElement);
      }
    },
  };
}
